import React, { useRef } from 'react';
import Draggable from 'react-draggable';
import { X, Minus, Square } from 'lucide-react';

export const Window = ({ 
  id,
  title, 
  children, 
  onClose, 
  onFocus,
  isActive = false,
  initialPosition = { x: 100, y: 100 },
  width = 600,
  height = 400,
  minWidth = 300,
  minHeight = 200,
  zIndex = 1,
  resizable = true
}) => {
  const nodeRef = useRef(null);

  const handleMouseDown = () => {
    onFocus(id);
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      handle=".window-handle"
      defaultPosition={initialPosition}
      bounds="parent"
    >
      <div
        ref={nodeRef}
        className="absolute"
        style={{ 
          zIndex,
          width: typeof width === 'number' ? `${width}px` : width,
          minWidth: `${minWidth}px`,
        }}
        onMouseDown={handleMouseDown}
      >
        <div 
          className={`
            window-border flex flex-col
            ${isActive ? 'ring-1 ring-primary/50' : ''}
          `}
          style={{
            height: typeof height === 'number' ? `${height}px` : height,
            minHeight: `${minHeight}px`,
            background: 'hsl(var(--card))',
          }}
        >
          {/* Title Bar */}
          <div 
            className={`
              window-handle flex items-center justify-between px-2 py-1 cursor-move select-none
              border-b border-border
              ${isActive ? 'bg-window-title-active' : 'bg-window-title'}
            `}
          >
            <div className="flex items-center gap-2">
              <span className="text-primary text-xs font-medium text-glow-subtle truncate">
                {title}
              </span>
            </div>
            
            <div className="flex items-center gap-1">
              {/* Minimize button - decorative */}
              <button 
                className="window-btn hover:bg-muted"
                onClick={(e) => e.stopPropagation()}
                title="Minimize"
              >
                <Minus className="w-2 h-2 text-primary" />
              </button>
              
              {/* Maximize button - decorative */}
              <button 
                className="window-btn hover:bg-muted"
                onClick={(e) => e.stopPropagation()}
                title="Maximize"
              >
                <Square className="w-2 h-2 text-primary" />
              </button>
              
              {/* Close button */}
              <button 
                className="window-btn hover:bg-destructive hover:text-destructive-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(id);
                }}
                title="Close"
              >
                <X className="w-2 h-2" />
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto bg-terminal-bg">
            {children}
          </div>

          {/* Resize Handle - decorative */}
          {resizable && (
            <div className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize opacity-50 hover:opacity-100">
              <svg viewBox="0 0 10 10" className="w-full h-full text-muted-foreground">
                <path d="M8 2 L8 8 L2 8" fill="none" stroke="currentColor" strokeWidth="1" />
                <path d="M6 4 L6 6 L4 6" fill="none" stroke="currentColor" strokeWidth="1" />
              </svg>
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
};
